import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";

import { todoApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const statusColors = {
  Open: "#1976d2",
  "In Progress": "#f57c00",
  Pending: "#7b1fa2",
  Closed: "#2e7d32"
};

const AdminTodoPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "Medium"
  });

  const [commentMap, setCommentMap] =
    useState({});

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    taskId: null,
    taskTitle: ""
  });

  // Check if user is admin
  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/");
    } else {
      fetchTodos();
    }
  }, [user, navigate]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const res = await todoApi.getAll();
      setTodos(res.data);
    } catch (err) {
      console.error("Error fetching todos:", err);
    } finally {
      setLoading(false);
    }
  };

  const createTodo = async () => {
    if (!form.title) return;

    await todoApi.create({
      ...form,
      createdBy: "Admin"
    });

    setForm({
      title: "",
      description: "",
      priority: "Medium"
    });

    fetchTodos();
  };

  const updateStatus = async (
    id,
    status
  ) => {
    await todoApi.updateStatus(id, status);

    fetchTodos();
  };

  const addComment = async (id) => {
    if (!commentMap[id]) return;

    await todoApi.addComment(id, {
      text: commentMap[id],
      addedBy: "Admin"
    });

    setCommentMap({
      ...commentMap,
      [id]: ""
    });

    fetchTodos();
  };

  const openDeleteConfirm = (id, title) => {
    setDeleteConfirm({
      open: true,
      taskId: id,
      taskTitle: title
    });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({
      open: false,
      taskId: null,
      taskTitle: ""
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.taskId) return;

    try {
      await todoApi.deleteTask(deleteConfirm.taskId);
      closeDeleteConfirm();
      fetchTodos();
    } catch (err) {
      console.error("Error deleting task:", err);
      closeDeleteConfirm();
    }
  };

  return (
    <Box
      sx={{
        p: 4,
        background: "#f7f7f7",
        minHeight: "100vh"
      }}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* HEADER */}
          <Typography
            variant="h4"
            fontWeight={900}
            mb={4}
          >
            Admin Task Manager
          </Typography>

      {/* CREATE TASK */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: "24px",
          mb: 5
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          mb={3}
        >
          Create New Task
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Task Title"
              value={form.title}
              onChange={(e) =>
                setForm({
                  ...form,
                  title: e.target.value
                })
              }
            />
          </Grid>

          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Description"
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description:
                    e.target.value
                })
              }
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <Select
                value={form.priority}
                onChange={(e) =>
                  setForm({
                    ...form,
                    priority:
                      e.target.value
                  })
                }
              >
                <MenuItem value="Low">
                  Low
                </MenuItem>

                <MenuItem value="Medium">
                  Medium
                </MenuItem>

                <MenuItem value="High">
                  High
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={createTodo}
              sx={{
                height: "100%"
              }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* TASK LIST */}
      <Stack spacing={3}>
        {todos.map((todo) => (
          <Paper
            key={todo._id}
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "24px"
            }}
          >
            {/* TOP */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              flexWrap="wrap"
              gap={2}
            >
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                >
                  {todo.title}
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  {todo.description}
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={1}
              >
                <Chip
                  label={todo.priority}
                  sx={{
                    background:
                      "#f4f4f4",
                    fontWeight: 700
                  }}
                />

                <Chip
                  label={todo.status}
                  sx={{
                    background:
                      statusColors[
                        todo.status
                      ],
                    color: "#fff",
                    fontWeight: 700
                  }}
                />
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* STATUS */}
            <Box mb={3}>
              <Typography
                fontWeight={700}
                mb={1}
              >
                Update Status
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
              >
                {[
                  "Open",
                  "In Progress",
                  "Pending",
                  "Closed"
                ].map((status) => (
                  <Button
                    key={status}
                    variant={
                      todo.status === status
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      updateStatus(
                        todo._id,
                        status
                      )
                    }
                  >
                    {status}
                  </Button>
                ))}
              </Stack>
            </Box>

            {/* COMMENTS */}
            <Box mb={3}>
              <Typography
                fontWeight={700}
                mb={2}
              >
                Comments
              </Typography>

              <Stack spacing={2}>
                {todo.comments.map(
                  (comment, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        background:
                          "#fafafa"
                      }}
                    >
                      <Typography>
                        {comment.text}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {comment.addedBy}
                      </Typography>
                    </Paper>
                  )
                )}
              </Stack>
            </Box>

            {/* ADD COMMENT */}
            <Grid
              container
              spacing={2}
            >
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Add comment..."
                  value={
                    commentMap[
                      todo._id
                    ] || ""
                  }
                  onChange={(e) =>
                    setCommentMap({
                      ...commentMap,
                      [todo._id]:
                        e.target.value
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() =>
                    addComment(todo._id)
                  }
                >
                  Add Comment
                </Button>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  onClick={() =>
                    openDeleteConfirm(
                      todo._id,
                      todo.title
                    )
                  }
                >
                  Delete
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Stack>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteConfirm.open}
        onClose={closeDeleteConfirm}
      >
        <DialogTitle>
          Delete Task?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the task <strong>"{deleteConfirm.taskTitle}"</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteConfirm}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default AdminTodoPage;